import React from "react";
import { useAuth } from "../auth.js";
import { TextField, Select, MenuItem } from "@mui/material";
import { fmsData, attributeLabels } from "@safelease/service-utilities";
import { Grid } from "@mui/material";
import { useQuery } from "@apollo/client";
import { getCubbyOrganizations } from "../queries";
import CircularProgress from "@mui/material/CircularProgress";
import Error from "./Error";
import { muiSelect } from "../styles/mui-overrides";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { MonumentService } from "../services/monument-service";
import { useQuery as useTanstackQuery } from "@tanstack/react-query";

interface FmsLoginFieldsProps {
  accountCredentials?: {
    displayName?: string;
    unsupportedFmsType?: string;
    corpCode?: string;
    url?: string;
    username?: string;
    password?: string;
  };
  accountType: string;
  credentialErrors: object;
  setFmsCredential: (attribute: string, value: string) => void;
}

const FmsLoginFields: React.FC<FmsLoginFieldsProps> = (props) => {
  const { setFmsCredential, credentialErrors, accountType, accountCredentials } = props;
  const auth = useAuth();
  const isAdmin = auth.user.isAdmin;

  const { loading: cubbyLoading, error: cubbyError, data: cubbyData } = useQuery(getCubbyOrganizations, { skip: accountType !== "cubby" });
  const {
    data: monumentOrganizations,
    isLoading: monumentOrganizationsLoading,
    error: monumentOrganizationsError,
  } = useTanstackQuery({
    queryKey: ["monumentOrganizations"],
    queryFn: async () => {
      const response = await MonumentService.getOrganizations();
      return response.data;
    },
    enabled: accountType === "monument",
  });

  // Display a loading indicator if we're waiting on cubby or monument data
  if ((accountType === "cubby" && cubbyLoading) || (accountType === "monument" && monumentOrganizationsLoading)) {
    return (
      <div style={{ textAlign: "center", width: "100%" }}>
        <CircularProgress />
      </div>
    );
  }

  if ((accountType === "cubby" && cubbyError) || (accountType === "monument" && monumentOrganizationsError)) return <Error />;

  return (
    <div className="modal-fields fms-login-fields">
      {!["cubby", "monument"].includes(accountType) && (
        <TextField
          className="modal-input-field"
          name="Display Name"
          label={attributeLabels.displayName}
          value={accountCredentials.displayName || ""}
          onChange={(e) => setFmsCredential("displayName", e.target.value)}
        />
      )}

      <Grid container spacing={2} alignItems="center" justifyContent="center" className="sections">
        {accountType &&
          Object.entries(fmsData[accountType].sections).map(([label, attributes], i) => {
            return (
              <Grid item xs={6} className="section" key={i} style={{ height: "100%" }}>
                <div className="section-label">{label}</div>
                {(attributes as string[]).map((attribute) => {
                  if (!isAdmin && accountType == "unsupported" && ["url", "username", "password"].includes(attribute)) return null;

                  if (accountType == "cubby" && attribute == "organizationId") {
                    return (
                      <FormControl key={attribute} style={{ marginBottom: "auto" }}>
                        <Select
                          displayEmpty
                          onChange={(e) => setFmsCredential(attribute, e.target.value)}
                          value={accountCredentials[attribute] || ""}
                          sx={muiSelect}
                        >
                          <MenuItem disabled value="">
                            <em>Select an organization</em>
                          </MenuItem>
                          {cubbyData.getCubbyOrganizations.map((organization) => {
                            return (
                              <MenuItem key={organization.id} value={organization.id}>
                                {organization.name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        <FormHelperText id="my-helper-text">{credentialErrors[attribute]?.msg}</FormHelperText>
                      </FormControl>
                    );
                  } else if (accountType == "monument" && attribute == "organizationId") {
                    return (
                      <FormControl key={attribute} style={{ marginTop: "16px" }}>
                        <Select
                          displayEmpty
                          onChange={(e) => setFmsCredential(attribute, e.target.value)}
                          value={accountCredentials[attribute] || ""}
                          sx={muiSelect}
                          renderValue={(selected) => {
                            if (!selected?.length) {
                              return <em>Select an organization</em>;
                            }

                            return selected;
                          }}
                        >
                          <MenuItem disabled value="">
                            <em>Select an organization</em>
                          </MenuItem>
                          {monumentOrganizations.map((organization) => {
                            return (
                              <MenuItem key={organization.id} value={organization.id}>
                                {organization.name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        <FormHelperText id="my-helper-text">{credentialErrors[attribute]?.msg}</FormHelperText>
                      </FormControl>
                    );
                  } else {
                    return (
                      <TextField
                        key={attribute}
                        className="modal-input-field"
                        name={attribute}
                        type={["password", "directPassword"].includes(attribute) ? "password" : "text"}
                        label={attributeLabels[attribute]}
                        value={accountCredentials[attribute] || ""}
                        onChange={(e) => setFmsCredential(attribute, e.target.value)}
                        helperText={credentialErrors[attribute]?.msg}
                      />
                    );
                  }
                })}
              </Grid>
            );
          })}
      </Grid>
    </div>
  );
};

export default FmsLoginFields;
